// SPDX-License-Identifier: AGPL-3.0-or-later
// Copyright (c) 2026 Password Manager contributors
import { useState, useEffect, useCallback, useRef } from 'react';
import { registerSW } from 'virtual:pwa-register';
import { useTranslation } from 'react-i18next';
import { Button } from '@/components/ui/button';
import { RefreshCw, CheckCircle, X } from 'lucide-react';
import { cn } from '@/lib/utils';
import { getLogger } from '@/lib/logger';

/**
 * PWAUpdatePrompt Component
 *
 * Shows prompts for:
 * - App ready for offline use
 * - New version available (with reload button)
 *
 * Uses vanilla virtual:pwa-register to avoid React hook context issues
 * that occur with the React hook variant (duplicate React instances)
 *
 * @example
 * ```tsx
 * // Add to App.tsx or main layout
 * <PWAUpdatePrompt />
 * ```
 */
export function PWAUpdatePrompt() {
  const { t } = useTranslation('common');
  const logger = getLogger();

  const [offlineReady, setOfflineReady] = useState(false);
  const [needRefresh, setNeedRefresh] = useState(false);
  const updateSWRef = useRef<((reloadPage?: boolean) => Promise<void>) | null>(null);

  useEffect(() => {
    if (!('serviceWorker' in navigator) || !navigator.serviceWorker) {
      logger.info('Service worker unavailable in this browser', {
        function: 'PWAUpdatePrompt',
      });
      return undefined;
    }

    // Register service worker with vanilla API
    const updateSW = registerSW({
      onNeedRefresh() {
        logger.info('New content available, refresh needed', {
          function: 'PWAUpdatePrompt',
        });
        setNeedRefresh(true);
      },
      onOfflineReady() {
        logger.info('App is ready for offline use', {
          function: 'PWAUpdatePrompt',
        });
        setOfflineReady(true);
      },
      onRegistered(registration) {
        logger.info('Service worker registered', {
          function: 'PWAUpdatePrompt',
          scope: registration?.scope,
        });

        // Check for updates every hour
        if (registration) {
          setInterval(() => {
            registration.update();
          }, 60 * 60 * 1000);
        }
      },
      onRegisterError(error) {
        logger.error('Service worker registration failed', {
          function: 'PWAUpdatePrompt',
          error: error instanceof Error ? error : undefined,
          message: error instanceof Error ? error.message : 'Unknown error',
        });
      },
    });

    // Store the update function for later use
    updateSWRef.current = updateSW;

    // Cleanup not needed - service worker persists
    return () => {
      updateSWRef.current = null;
    };
  }, [logger]);

  const handleReload = useCallback(async () => {
    logger.info('User triggered PWA update reload', {
      function: 'PWAUpdatePrompt',
    });
    try {
      await updateSWRef.current?.(true);
    } finally {
      window.location.reload();
    }
  }, [logger]);

  const handleDismissOfflineReady = useCallback(() => {
    setOfflineReady(false);
    logger.info('Offline ready notification dismissed', {
      function: 'PWAUpdatePrompt',
    });
  }, [logger]);

  const handleDismissNeedRefresh = useCallback(() => {
    setNeedRefresh(false);
    logger.info('Update notification dismissed', {
      function: 'PWAUpdatePrompt',
    });
  }, [logger]);

  // Don't render if nothing to show
  if (!offlineReady && !needRefresh) {
    return null;
  }

  return (
    <div
      role="alert"
      aria-live="polite"
      className={cn(
        'fixed bottom-4 start-4 end-4 z-50',
        'sm:start-auto sm:end-4 sm:max-w-sm',
        'animate-in slide-in-from-bottom-4 fade-in duration-300'
      )}
    >
      {/* Offline Ready Toast */}
      {offlineReady && (
        <div
          className={cn(
            'flex items-center gap-3 p-4 rounded-lg shadow-lg',
            'bg-card text-card-foreground border border-border',
            'mb-2'
          )}
        >
          <CheckCircle
            className="h-5 w-5 text-green-500 flex-shrink-0"
            aria-hidden="true"
          />
          <p className="flex-1 text-sm font-medium">
            {t('pwa.offline_ready.toast', 'Ready to work offline.')}
          </p>
          <Button
            variant="ghost"
            size="icon"
            className="h-8 w-8 flex-shrink-0"
            onClick={handleDismissOfflineReady}
            aria-label={t('common.dismiss', 'Dismiss')}
          >
            <X className="h-4 w-4" />
          </Button>
        </div>
      )}

      {/* Update Available Toast */}
      {needRefresh && (
        <div
          className={cn(
            'flex items-center gap-3 p-4 rounded-lg shadow-lg',
            'bg-card text-card-foreground border border-border'
          )}
        >
          <RefreshCw
            className="h-5 w-5 text-primary flex-shrink-0"
            aria-hidden="true"
          />
          <p className="flex-1 text-sm font-medium">
            {t('pwa.update_available.title', 'New version available')}
          </p>
          <p className="sr-only">
            {t('pwa.update_available.description', 'Refresh to get the latest improvements.')}
          </p>
          <div className="flex items-center gap-2 flex-shrink-0">
            <Button
              variant="default"
              size="sm"
              className="h-8 min-w-[80px]"
              onClick={() => void handleReload()}
            >
              {t('pwa.update_available.cta_update', 'Update')}
            </Button>
            <Button
              variant="ghost"
              size="icon"
              className="h-8 w-8"
              onClick={handleDismissNeedRefresh}
              aria-label={t('pwa.update_available.cta_later', 'Later')}
            >
              <X className="h-4 w-4" />
            </Button>
          </div>
        </div>
      )}
    </div>
  );
}

export default PWAUpdatePrompt;
